import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  FileDown,
  Send,
  Copy,
  Ban,
  DollarSign,
  FileText,
  PackagePlus,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Checkbox } from '@/app/components/ui/checkbox';
import { Skeleton } from '@/app/components/ui/skeleton';
import { useIndustry } from '@/hooks/use-industry';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Alert,
  AlertDescription,
} from '@/app/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/app/components/ui/alert-dialog';
import {
  useInvoice,
  useSendInvoice,
  useVoidInvoice,
  useRecordPayment,
  useDuplicateInvoice,
  useDownloadInvoicePdf,
  useMarkInvoicePaid,
  usePullInvoiceExpenses,
} from '@/hooks/use-projects';
import { handleApiError } from '@/lib/api-client';
import type { InvoiceStatus, InvoiceLineType, PaymentMethod } from '@/lib/types';
import { formatMoney } from '@/lib/currency';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import { useOrgCurrency, useOrgTimeZone } from '@/hooks/use-settings';
import { getLocalToday } from '@/lib/date-utils';

const statusColors: Record<InvoiceStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  viewed: 'bg-indigo-100 text-indigo-800',
  paid: 'bg-green-100 text-green-800',
  partial: 'bg-yellow-100 text-yellow-800',
  overdue: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-600',
  void: 'bg-gray-100 text-gray-600',
};

const statusLabels: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  viewed: 'Viewed',
  paid: 'Paid',
  partial: 'Partial',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
  void: 'Void',
};

const lineTypeLabels: Record<InvoiceLineType, string> = {
  labor: 'Labor',
  equipment: 'Equipment',
  materials: 'Materials',
  travel: 'Travel',
  per_diem: 'Per Diem',
  expense: 'Expense',
  other: 'Other',
};

export function InvoiceDetailPage() {
  const currency = useOrgCurrency();
  const timeZone = useOrgTimeZone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const invoiceId = parseInt(id || '0', 10);
  const { data: invoice, isLoading } = useInvoice(invoiceId);
  const { labels } = useIndustry();

  const sendMutation = useSendInvoice();
  const voidMutation = useVoidInvoice();
  const duplicateMutation = useDuplicateInvoice();
  const downloadPdfMutation = useDownloadInvoicePdf();
  const markPaidMutation = useMarkInvoicePaid();
  const recordPaymentMutation = useRecordPayment();
  const pullExpensesMutation = usePullInvoiceExpenses();
  const [pullMessage, setPullMessage] = useState<string | null>(null);

  const [actionError, setActionError] = useState<string | null>(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [includeEvidence, setIncludeEvidence] = useState(false);

  // Payment form state
  const [payAmount, setPayAmount] = useState('');
  const [payDate, setPayDate] = useState(getLocalToday());
  const [payMethod, setPayMethod] = useState<PaymentMethod>('check');
  const [payReference, setPayReference] = useState('');
  const [payNotes, setPayNotes] = useState('');

  const isDraft = invoice?.status === 'draft';
  const canSend = isDraft;
  const canRecordPayment = invoice && ['sent', 'viewed', 'partial', 'overdue'].includes(invoice.status);
  const canVoid = invoice && invoice.status !== 'void' && invoice.status !== 'paid';
  const canMarkPaid = invoice && ['sent', 'viewed', 'partial', 'overdue'].includes(invoice.status);

  const handlePullExpenses = () => {
    if (!invoice) return;
    setActionError(null);
    setPullMessage(null);
    pullExpensesMutation.mutate(invoice.id, {
      onSuccess: (data) => {
        const added = data.invoice_lines_created ?? 0;
        setPullMessage(
          added > 0
            ? `Added ${added} ${added === 1 ? 'line' : 'lines'} from approved billable expenses and received PO costs.`
            : 'Nothing new to pull.'
        );
      },
      onError: (err) =>
        setActionError(handleApiError(err).message || 'Failed to pull expenses.'),
    });
  };

  const handleSend = () => {
    if (!invoice) return;
    setActionError(null);
    sendMutation.mutate(invoice.id, {
      onError: () => setActionError('Failed to send invoice.'),
    });
  };

  const handleVoid = () => {
    if (!invoice) return;
    setActionError(null);
    voidMutation.mutate(invoice.id, {
      onError: () => setActionError('Failed to void invoice.'),
    });
  };

  const handleDuplicate = () => {
    if (!invoice) return;
    setActionError(null);
    duplicateMutation.mutate(invoice.id, {
      onSuccess: (newInvoice) => navigate(`/projects/invoices/${newInvoice.id}`),
      onError: () => setActionError('Failed to duplicate invoice.'),
    });
  };

  const handleDownloadPdf = () => {
    if (!invoice) return;
    downloadPdfMutation.mutate({ id: invoice.id, includeEvidence });
  };

  const handleMarkPaid = () => {
    if (!invoice) return;
    setActionError(null);
    markPaidMutation.mutate(invoice.id, {
      onError: () => setActionError('Failed to mark invoice as paid.'),
    });
  };

  const handleRecordPayment = () => {
    if (!invoice || !payAmount) return;
    setActionError(null);
    recordPaymentMutation.mutate(
      {
        invoice: invoice.id,
        amount: payAmount,
        payment_date: payDate,
        payment_method: payMethod,
        reference_number: payReference,
        notes: payNotes,
      },
      {
        onSuccess: () => {
          setShowPaymentForm(false);
          setPayAmount('');
          setPayReference('');
          setPayNotes('');
        },
        onError: () => setActionError('Failed to record payment.'),
      }
    );
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-600">Invoice not found.</p>
        <Button asChild className="mt-4">
          <Link to="/projects/invoices">Back to Invoices</Link>
        </Button>
      </div>
    );
  }

  const lineItems = invoice.line_items || [];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/projects/invoices')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{invoice.invoice_number}</h1>
              <Badge className={statusColors[invoice.status]}>
                {statusLabels[invoice.status]}
              </Badge>
            </div>
            <p className="text-gray-600">
              {invoice.client_name} &middot; {invoice.project_name}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadPdf}
              disabled={downloadPdfMutation.isPending}
            >
              <FileDown className="h-4 w-4 mr-1" />
              PDF
            </Button>
            <Button variant="outline" size="sm" onClick={handleDuplicate} disabled={duplicateMutation.isPending}>
              <Copy className="h-4 w-4 mr-1" />
              Duplicate
            </Button>
            {canRecordPayment && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPaymentForm(!showPaymentForm)}
              >
                <DollarSign className="h-4 w-4 mr-1" />
                Record Payment
              </Button>
            )}
            {canMarkPaid && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" className="bg-green-600 hover:bg-green-700">
                    <DollarSign className="h-4 w-4 mr-1" />
                    Mark Paid
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Mark Invoice as Paid</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will mark the full amount of {formatMoney(invoice.total, currency)} as paid.
                      Use &quot;Record Payment&quot; instead for partial payments.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleMarkPaid} disabled={markPaidMutation.isPending}>
                      {markPaidMutation.isPending ? 'Updating...' : 'Mark as Paid'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {isDraft && (
              <Button
                variant="outline"
                size="sm"
                onClick={handlePullExpenses}
                disabled={pullExpensesMutation.isPending}
              >
                <PackagePlus className="h-4 w-4 mr-1" />
                {pullExpensesMutation.isPending ? 'Pulling…' : 'Pull expenses'}
              </Button>
            )}
            {canSend && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm">
                    <Send className="h-4 w-4 mr-1" />
                    Send Invoice
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Send Invoice</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will mark the invoice as sent and record the timestamp.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleSend} disabled={sendMutation.isPending}>
                      {sendMutation.isPending ? 'Sending...' : 'Send Invoice'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {canVoid && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm">
                    <Ban className="h-4 w-4 mr-1" />
                    Void
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Void Invoice</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. The invoice will be permanently voided.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleVoid} disabled={voidMutation.isPending}>
                      {voidMutation.isPending ? 'Voiding...' : 'Void Invoice'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="include-evidence-bundle"
              checked={includeEvidence}
              onCheckedChange={(checked) => setIncludeEvidence(Boolean(checked))}
              disabled={downloadPdfMutation.isPending}
            />
            <Label htmlFor="include-evidence-bundle" className="text-sm font-normal text-gray-600">
              Include evidence bundle
            </Label>
          </div>
        </div>
      </div>

      {actionError && (
        <Alert variant="destructive">
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}

      {pullMessage && (
        <Alert>
          <AlertDescription>{pullMessage}</AlertDescription>
        </Alert>
      )}

      {invoice.status === 'void' && (
        <Alert>
          <FileText className="h-4 w-4" />
          <AlertDescription>
            This invoice has been voided and is no longer active.
          </AlertDescription>
        </Alert>
      )}

      {/* Record Payment Form */}
      {showPaymentForm && (
        <Card>
          <CardHeader>
            <CardTitle>Record Payment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label>Amount *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  placeholder={invoice.balance_due}
                  required
                />
              </div>
              <div>
                <Label>Payment Date *</Label>
                <Input
                  type="date"
                  value={payDate}
                  onChange={(e) => setPayDate(e.target.value)}
                />
              </div>
              <div>
                <Label>Payment Method</Label>
                <Select
                  value={payMethod}
                  onValueChange={(v) => setPayMethod(v as PaymentMethod)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="wire">Wire Transfer</SelectItem>
                    <SelectItem value="ach">ACH</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Reference Number</Label>
                <Input
                  value={payReference}
                  onChange={(e) => setPayReference(e.target.value)}
                  placeholder="Check #, Transaction ID, etc."
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Input
                  value={payNotes}
                  onChange={(e) => setPayNotes(e.target.value)}
                  placeholder="Optional notes"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleRecordPayment}
                disabled={recordPaymentMutation.isPending}
              >
                {recordPaymentMutation.isPending ? 'Recording...' : 'Record Payment'}
              </Button>
              <Button variant="outline" onClick={() => setShowPaymentForm(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoice Info */}
      <Card>
        <CardHeader>
          <CardTitle>Invoice Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500 block">Invoice Number</span>
              <span className="font-medium">{invoice.invoice_number}</span>
            </div>
            <div>
              <span className="text-gray-500 block">{labels.client}</span>
              <span className="font-medium">{invoice.client_name}</span>
            </div>
            <div>
              <span className="text-gray-500 block">Project</span>
              <span className="font-medium">{invoice.project_name}</span>
            </div>
            <div>
              <span className="text-gray-500 block">Issue Date</span>
              <span className="font-medium">
                {formatDateOnly(invoice.issue_date)}
              </span>
            </div>
            <div>
              <span className="text-gray-500 block">Due Date</span>
              <span className="font-medium">
                {formatDateOnly(invoice.due_date)}
              </span>
            </div>
            {invoice.sent_at && (
              <div>
                <span className="text-gray-500 block">Sent</span>
                <span className="font-medium">
                  {formatDateTimeInTimeZone(invoice.sent_at, timeZone, { dateStyle: 'medium' })}
                </span>
              </div>
            )}
            {invoice.paid_at && (
              <div>
                <span className="text-gray-500 block">Paid</span>
                <span className="font-medium">
                  {formatDateTimeInTimeZone(invoice.paid_at, timeZone, { dateStyle: 'medium' })}
                </span>
              </div>
            )}
            <div>
              <span className="text-gray-500 block">Created</span>
              <span className="font-medium">
                {formatDateTimeInTimeZone(invoice.created_at, timeZone, { dateStyle: 'medium' })}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Line Items */}
      <Card>
        <CardHeader>
          <CardTitle>Line Items</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead className="text-right">Unit Price</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lineItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="text-gray-600">
                    {lineTypeLabels[item.line_type]}
                  </TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell className="text-right">{item.quantity}</TableCell>
                  <TableCell className="text-gray-600">{item.unit}</TableCell>
                  <TableCell className="text-right">{formatMoney(item.unit_price, currency)}</TableCell>
                  <TableCell className="text-right font-medium">
                    {formatMoney(item.total, currency)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Totals */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col items-end gap-2 text-sm">
            <div className="flex gap-8">
              <span className="text-gray-600">Subtotal:</span>
              <span className="font-medium w-32 text-right">{formatMoney(invoice.subtotal, currency)}</span>
            </div>
            {parseFloat(invoice.tax_rate) > 0 && (
              <div className="flex gap-8">
                <span className="text-gray-600">Tax ({invoice.tax_rate}%):</span>
                <span className="font-medium w-32 text-right">
                  {formatMoney(invoice.tax_amount, currency)}
                </span>
              </div>
            )}
            <div className="flex gap-8 text-base font-bold border-t pt-2 mt-1">
              <span>Total:</span>
              <span className="w-32 text-right">{formatMoney(invoice.total, currency)}</span>
            </div>
            {parseFloat(invoice.retainage_amount) > 0 && !invoice.is_retainage_release && (
              <div className="flex gap-8 text-sm text-orange-700">
                <span>Retainage Withheld:</span>
                <span className="w-32 text-right">{formatMoney(invoice.retainage_amount, currency)}</span>
              </div>
            )}
            {invoice.is_retainage_release && (
              <div className="flex gap-8 text-sm text-green-700">
                <span>Retainage Release</span>
              </div>
            )}
            {parseFloat(invoice.amount_paid) > 0 && (
              <div className="flex gap-8">
                <span className="text-gray-600">Payments Received:</span>
                <span className="font-medium w-32 text-right text-green-600">
                  ({formatMoney(invoice.amount_paid, currency)})
                </span>
              </div>
            )}
            <div className="flex gap-8 text-lg font-bold border-t pt-2 mt-1">
              <span>Balance Due:</span>
              <span className="w-32 text-right">{formatMoney(invoice.balance_due, currency)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Instructions */}
      {invoice.payment_instructions && (
        <Card>
          <CardHeader>
            <CardTitle>Payment Instructions</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {invoice.payment_instructions}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Notes */}
      {invoice.notes && (
        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{invoice.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
